import { createElement, type ReactNode } from "react";

/**
 * Render a chat body with @mentions highlighted, keeping the author's
 * SPACING: line breaks, blank lines and indentation come through verbatim
 * (`whitespace-pre-wrap`), so a pasted log paragraph reads the way it was
 * written instead of collapsing into one run-on line.
 *
 * Mentions are matched against the message's OWN `mentions` list first
 * (`@Arjun Liljo` as one token), falling back to a bare `@word` when the
 * message carries no list — the old regex guessed "@word word" and coloured
 * the word after a mention by accident.
 */
const BODY_CLASS =
  "whitespace-pre-wrap break-words [overflow-wrap:anywhere] [tab-size:4]";

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function renderChatBody(
  text: string,
  mentions?: { userId: number; name: string }[] | null,
  mentionClassName = "text-[#218760] font-semibold",
): ReactNode[] {
  const names = (mentions ?? [])
    .map((m) => m?.name)
    .filter((n): n is string => typeof n === "string" && n.length > 0)
    .sort((a, b) => b.length - a.length);
  const pattern =
    names.length > 0
      ? `(@(?:${names.map(escapeRegExp).join("|")}))(?![\\w])`
      : "(@[\\w.-]+)";
  const re = new RegExp(pattern, "g");
  const parts = text.split(re);
  return parts.map((part, i) =>
    part && part.startsWith("@") && i % 2 === 1
      ? createElement("span", { key: i, className: mentionClassName }, part)
      : createElement("span", { key: i }, part),
  );
}

/** Tailwind classes the body `<p>` needs so the spacing survives. */
export const CHAT_BODY_CLASS = BODY_CLASS;
